/**
 * HeaderSubmenu Module
 * Supports both hover (desktop) and click (touch devices)
 * Pure Tailwind, no inline styles
 */

import { debugLog } from '../debug/logger.js'

class HeaderSubmenu {
	constructor() {
		this.CONFIG = {
			SELECTORS: {
				submenu: '.ui-mm',
				navigation: '.ui-nav-desktop',
				menuItems: 'li.group',
			},
			TIMING: {
				POSITION_DELAY: 50,
				HOVER_CLOSE_DELAY: 50,
			},
		}

		this.state = {
			currentOpenMenu: null,
			closeTimer: null,
			positioned: new Set(),
		}

		this.elements = this.getElements()

		if (this.elements.submenus.length > 0) {
			debugLog.success('HeaderSubmenu: Found', this.elements.submenus.length, 'submenus')
			this.init()
		} else {
			debugLog.warn('HeaderSubmenu: No submenus found')
		}
	}

	getElements() {
		const { SELECTORS } = this.CONFIG
		return {
			submenus: document.querySelectorAll(SELECTORS.submenu),
			navigation: document.querySelector(SELECTORS.navigation),
			menuItems: document.querySelectorAll(SELECTORS.menuItems),
		}
	}

	init() {
		setTimeout(() => this.positionSubmenus(), this.CONFIG.TIMING.POSITION_DELAY)
		this.bindEvents()
		this.bindWindowEvents()
	}

	bindEvents() {
		this.elements.menuItems.forEach(menuItem => {
			const submenu = menuItem.querySelector(this.CONFIG.SELECTORS.submenu)
			if (!submenu) return

			const link = menuItem.querySelector('a')
			if (!link) return

			menuItem.addEventListener('mouseleave', e => {
				// Don't close if moving to submenu
				if (e.relatedTarget && submenu.contains(e.relatedTarget)) return
				this.scheduleClose(menuItem, submenu)
			})

			submenu.addEventListener('mouseenter', () => this.cancelClose())
			submenu.addEventListener('mouseleave', () => this.scheduleClose(menuItem, submenu))

			// Open submenu on first click, navigate on second click.
			link.addEventListener('click', e => {
				const isOpen = menuItem.hasAttribute('open')

				if (isOpen) {
					return
				}

				e.preventDefault()
				this.openMenu(menuItem, submenu)
			})
		})

		// Close on click outside
		document.addEventListener('click', e => {
			const clickedItem = e.target.closest('li.group')
			if (!clickedItem && this.state.currentOpenMenu) {
				this.closeMenu(this.state.currentOpenMenu)
			}
		})

		// Close on ESC key
		document.addEventListener('keydown', e => {
			if (e.key === 'Escape' && this.state.currentOpenMenu) {
				this.closeMenu(this.state.currentOpenMenu)
			}
		})
	}

	openMenu(menuItem, submenu) {
		// Close any other open menu first
		if (this.state.currentOpenMenu && this.state.currentOpenMenu !== menuItem) {
			this.closeMenu(this.state.currentOpenMenu)
		}

		this.cancelClose()

		// Open menu - instant show with display block
		submenu.classList.remove('hidden')
		menuItem.setAttribute('open', 'true')

		// Ensure pointer events are enabled immediately
		submenu.style.pointerEvents = 'auto'

		const link = menuItem.querySelector('a[aria-expanded]')
		link?.setAttribute('aria-expanded', 'true')

		// Rotate the arrow icon
		const arrow = menuItem.querySelector('a > svg')
		if (arrow) {
			arrow.classList.add('rotate-180')
		}

		this.state.currentOpenMenu = menuItem

		// Position if first time
		if (!this.state.positioned.has(menuItem)) {
			this.positionSubmenus()
			this.state.positioned.add(menuItem)
		}
	}

	closeMenu(menuItem) {
		const submenu = menuItem.querySelector(this.CONFIG.SELECTORS.submenu)
		if (!submenu) return

		submenu.classList.add('hidden')

		menuItem.removeAttribute('open')
		submenu.style.pointerEvents = ''

		const link = menuItem.querySelector('a[aria-expanded]')
		link?.setAttribute('aria-expanded', 'false')

		// Rotate the arrow icon back
		const arrow = menuItem.querySelector('a > svg')
		if (arrow) {
			arrow.classList.remove('rotate-180')
		}

		if (this.state.currentOpenMenu === menuItem) {
			this.state.currentOpenMenu = null
		}
	}

	scheduleClose(menuItem, submenu) {
		this.cancelClose()
		this.state.closeTimer = setTimeout(() => {
			this.closeMenu(menuItem)
		}, this.CONFIG.TIMING.HOVER_CLOSE_DELAY)
	}

	cancelClose() {
		if (this.state.closeTimer) {
			clearTimeout(this.state.closeTimer)
			this.state.closeTimer = null
		}
	}

	bindWindowEvents() {
		let resizeTimer
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer)
			resizeTimer = setTimeout(() => this.positionSubmenus(), 150)
		})

		let scrollTimer
		window.addEventListener('scroll', () => {
			clearTimeout(scrollTimer)
			scrollTimer = setTimeout(() => this.positionSubmenus(), 100)
		})
	}

	positionSubmenus() {
		this.elements.submenus.forEach(submenu => {
			const parentElement = submenu.closest('.group')
			if (!parentElement) return

			const parentRect = parentElement.getBoundingClientRect()
			const offsetX = -parentRect.left

			const classes = ['absolute', 'top-full', 'w-screen', 'bg-white', 'shadow-lg', 'border-t', 'border-gray-200', 'z-50']

			classes.forEach(className => {
				if (!submenu.classList.contains(className)) {
					submenu.classList.add(className)
				}
			})

			submenu.style.left = `${offsetX}px`
			submenu.style.transform = ''
		})
	}
}

// Initialize
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new HeaderSubmenu())
} else {
	new HeaderSubmenu()
}

window.HeaderSubmenu = HeaderSubmenu
